import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import { useAuth } from '../../context/AuthContext';
import { ICoupon } from '../../types/coupon';
import { ICouponStats } from '../../types/couponStats';
import NotificationEditDialog from './NotificationEditDialog';
import StatsDialog from '../shared/StatsDialog';
import { couponService } from '../../services/couponService';

interface INotification {
  couponId: string;
  notificationText: string;
  description: string;
  dateCreated: string;
  totalCount: number;
  sentCount: number;
  receivedCount: number;
  viewedCount: number;
  isDeleted: boolean;
}

const NotificationsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [description, setDescription] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<ICoupon | null>(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsData, setStatsData] = useState<ICouponStats[]>([]);
  const [statsNotification, setStatsNotification] = useState<ICoupon | null>(null);
  const { hasRole } = useAuth();

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        notification_type: 'notification'
      };
      if (notificationTitle) params.notificationTitle = notificationTitle;
      if (description) params.notificationDescription = description;

      const response = await axios.get(`${environment.apiUrl}coupon`, { params });
      console.log('Notifications response:', response.data);

      const notificationsData = Array.isArray(response.data) ? response.data : [];
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [notificationTitle, description]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleSearch = () => {
    setPage(0);
    loadNotifications();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedNotifications = notifications.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleHideNotification = async (notification: INotification) => {
    if (window.confirm('Are you sure you want to make this INACTIVE?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${notification.couponId}`);
        loadNotifications();
      } catch (error) {
        console.error('Error hiding notification:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleUnhideNotification = async (notification: INotification) => {
    if (window.confirm('Are you sure you want to make this ACTIVE again?')) {
      try {
        await axios.put(`${environment.apiUrl}coupon/${notification.couponId}`);
        loadNotifications();
      } catch (error) {
        console.error('Error unhiding notification:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleDeleteNotification = async (notification: INotification) => {
    if (window.confirm('Are you sure you want to delete it permanently?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${notification.couponId}?force=true`);
        loadNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const showDeleteButton = (notification: INotification) => {
    return notification.isDeleted && hasRole(['notification:admin', 'notification:tester']);
  };

  const handleEditNotification = (notification: INotification) => {
    setSelectedNotification(notification as ICoupon);
    setEditDialogOpen(true);
  };

  const handleNewNotification = () => {
    setSelectedNotification(null);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedNotification(null);
  };

  const handleNotificationSaved = () => {
    loadNotifications();
  };

  const handleShowStats = async (notification: INotification) => {
    try {
      setLoading(true);
      const stats = await couponService.getStats(notification.couponId!, 'notification');
      setStatsData(stats);
      setStatsNotification(notification as ICoupon);
      setStatsDialogOpen(true);
    } catch (error) {
      console.error('Error loading stats:', error);
      alert('Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleStatsDialogClose = () => {
    setStatsDialogOpen(false);
    setStatsData([]);
    setStatsNotification(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Notifications
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewNotification}
          sx={{ minWidth: '160px' }}
        >
          New Notification
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <TextField
            label="Notification Title"
            value={notificationTitle}
            onChange={(e) => setNotificationTitle(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ minWidth: '120px', height: '40px' }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {paginatedNotifications.map((notification) => (
              <Card 
                key={notification.couponId} 
                sx={{ 
                  mb: 2, 
                  border: notification.isDeleted ? '1px solid' : 'none',
                  borderColor: notification.isDeleted ? 'error.main' : 'transparent',
                  backgroundColor: notification.isDeleted ? 'rgba(244, 67, 54, 0.08)' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: notification.isDeleted ? 'error.main' : 'inherit' }}>
                      {notification.notificationText}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" color="primary" onClick={() => handleEditNotification(notification)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="info" onClick={() => handleShowStats(notification)}>
                        <BarChartIcon fontSize="small" />
                      </IconButton>
                      {!notification.isDeleted ? (
                        <IconButton size="small" color="warning" onClick={() => handleHideNotification(notification)}>
                          <VisibilityOffIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" color="success" onClick={() => handleUnhideNotification(notification)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {showDeleteButton(notification) ? (
                        <IconButton size="small" color="error" onClick={() => handleDeleteNotification(notification)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Description:</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', maxWidth: '60%' }}>
                        {notification.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date Created:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(notification.dateCreated).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="body2" fontWeight={600}>{notification.totalCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Sent:</Typography>
                      <Typography variant="body2" fontWeight={500}>{notification.sentCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Received:</Typography>
                      <Typography variant="body2" fontWeight={500}>{notification.receivedCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Viewed:</Typography>
                      <Typography variant="body2" fontWeight={500}>{notification.viewedCount}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={notifications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>

          {/* Desktop Table View */}
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Notification Text</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Date Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Received</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Viewed</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {paginatedNotifications.map((notification) => (
                  <TableRow
                    key={notification.couponId}
                    sx={{ 
                      color: notification.isDeleted ? 'error.main' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit', wordBreak: 'break-word' }}>
                      {notification.notificationText}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit', wordBreak: 'break-word' }}>
                      {notification.description}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit' }}>
                      {new Date(notification.dateCreated).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit' }}>
                      {notification.totalCount}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit' }}>
                      {notification.sentCount}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit' }}>
                      {notification.receivedCount}
                    </TableCell>
                    <TableCell sx={{ color: notification.isDeleted ? 'red' : 'inherit' }}>
                      {notification.viewedCount}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditNotification(notification)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Statistics">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleShowStats(notification)}
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {!notification.isDeleted ? (
                          <Tooltip title="Make Inactive">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleHideNotification(notification)}
                            >
                              <VisibilityOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Make Active">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUnhideNotification(notification)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showDeleteButton(notification) ? (
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteNotification(notification)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={notifications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </TableContainer>
          </Paper>
        </>
      )}

      <NotificationEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        notification={selectedNotification}
        onSaved={handleNotificationSaved}
      />

      <StatsDialog
        open={statsDialogOpen}
        onClose={handleStatsDialogClose}
        coupon={statsNotification}
        stats={statsData}
      />
    </Box>
  );
};

export default NotificationsList;
